import { AltsAllocation } from "@/types/investment";
import AllocationHoverCard from "./AllocationHoverCard";

const altDescriptions = {
  "Private Equity": {
    introduction: "Private Equity involves investing in privately held companies or buyouts of public companies. It offers significant potential for growth by fostering innovation, restructuring, or scaling businesses, often over a long-term horizon.",
    liquidityPosition: "Very Low",
    liquidityExplanation: "Private Equity investments typically have strict lock-up periods, making them suited for long-term goals such as wealth growth or legacy planning."
  },
  "Private Credit": {
    introduction: "Private Credit refers to non-bank lending, such as loans to middle-market companies or structured credit. Investors benefit from consistent cash flows and attractive returns, typically as a higher-risk alternative to traditional bonds.",
    liquidityPosition: "Low",
    liquidityExplanation: "Often subject to long lock-up periods, these investments align with long-term income-generating goals."
  },
  "Private Debt": {
    introduction: "Private Debt involves financing businesses through loans or debt instruments not traded on public markets. It provides steady returns and diversification, especially during times of market volatility.",
    liquidityPosition: "Low",
    liquidityExplanation: "Like Private Credit, Private Debt investments often require significant lock-up periods and are tailored for long-term strategies."
  },
  "Real Estate": {
    introduction: "Real Estate investments include direct property ownership, Real Estate Investment Trusts (REITs), and private equity real estate funds. They provide income and diversification, often hedging against inflation.",
    liquidityPosition: "Moderate to Low",
    liquidityExplanation: "Liquidity depends on the investment structure; direct ownership or funds typically require longer holding periods."
  },
  "Hedge Funds": {
    introduction: "Hedge Funds are specialized investment vehicles using advanced strategies to pursue returns in diverse market conditions. They offer flexibility and diversification beyond traditional assets, aiming to manage risk while enhancing returns.",
    liquidityPosition: "Low to Moderate",
    liquidityExplanation: "Redemption periods can vary significantly based on strategy, ranging from quarterly to multi-year, requiring a longer-term outlook."
  },
  "Commodities": {
    introduction: "Commodities encompass physical goods such as precious metals, oil, or agricultural products. They act as an inflation hedge and portfolio diversifier, typically tied to global supply and demand.",
    liquidityPosition: "High to Moderate",
    liquidityExplanation: "Futures and ETFs provide high liquidity, while direct investment in physical goods or private funds may have restrictions."
  },
  "Cryptocurrencies": {
    introduction: "Cryptocurrencies represent a digital asset class leveraging blockchain technology. Known for high volatility, they offer opportunities for speculative returns and diversification into emerging technologies.",
    liquidityPosition: "High to Medium-High",
    liquidityExplanation: "Cryptocurrencies are often highly liquid, with daily trading available, though some investment vehicles (e.g., funds) may impose restrictions."
  },
  "Collectibles": {
    introduction: "Collectibles include art, wine, rare coins, and other unique items. They offer potential appreciation and enjoyment value, while serving as a store of value independent of financial markets.",
    liquidityPosition: "Low",
    liquidityExplanation: "Market-dependent liquidity with potentially long selling periods, requiring expertise in specific collectible markets."
  }
} as const;

interface AltAllocationDisplayProps {
  altAllocations: AltsAllocation | null;
}

const AltAllocationDisplay = ({ altAllocations }: AltAllocationDisplayProps) => {
  if (!altAllocations) return null;

  const allocations = [
    { name: 'Private Equity', value: altAllocations.private_equity_allocation },
    { name: 'Private Credit', value: altAllocations.private_credit_allocation },
    { name: 'Private Debt', value: altAllocations.private_debt_allocation },
    { name: 'Real Estate', value: altAllocations.real_estate_allocation },
    { name: 'Hedge Funds', value: altAllocations.hedge_funds_allocation },
    { name: 'Commodities', value: altAllocations.commodities_allocation },
    { name: 'Cryptocurrencies', value: altAllocations.cryptocurrencies_allocation },
    { name: 'Collectibles', value: altAllocations.collectibles_allocation }
  ];

  return (
    <div className="space-y-3">
      <h3 className="text-navy-dark font-medium">Default Alternative Allocations</h3>
      <div className="grid grid-cols-2 gap-4">
        {allocations.map((allocation) => (
          <AllocationHoverCard 
            key={allocation.name} 
            allocation={allocation}
            description={altDescriptions[allocation.name as keyof typeof altDescriptions]}
          />
        ))}
      </div>
    </div>
  );
};

export default AltAllocationDisplay;